import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { TABLE_HEAD_ROW_SX } from '../../../Utils/format';
import PageHeader from '../../../Components/GatewayContagem/PageHeader';
import { userAPI } from '../../../API/userAPI';
import { gatewayContagemAPI } from '../../../API/gatewayContagemAPI';
import GatewayContagemContext from '../../../Context/GatewayContagemContext';
import usePermissions from '../../../Context/Hooks/usePermissions';
import { PERMISSIONS } from '../../../Constants/permissions';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemText,

  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  AdminPanelSettingsOutlined as AdminPanelSettingsOutlinedIcon,
  EmailOutlined as EmailOutlinedIcon,
  FileDownloadOutlined as FileDownloadIcon,
  GroupOutlined as GroupOutlinedIcon,
  MoreHoriz as MoreHorizIcon,
  PersonAddAlt1Outlined as PersonAddAlt1OutlinedIcon,
  PersonOutlined as PersonOutlinedIcon,
  Search as SearchIcon,
  TodayOutlined as TodayOutlinedIcon,
} from '@mui/icons-material';
import StatCard from '../../../Components/GatewayContagem/StatCard';
import LoadingSkeleton from '../../../Components/GatewayContagem/LoadingSkeleton';
import { VISIBLE_PROFILES, ROLE_LEVEL, NOTIF_CATEGORIES, getAssignableProfileCodes, isEntraAuthProvider } from '../../../Constants/userConstants';
import InviteUserDialog from '../../../Components/Modal/InviteUserDialog';
import ModalTemplate from '../../../Components/Modal/ModalTemplate';

// ─── Profile creation constants ───────────────────────────────────────────────

const MODULE_IDS = [
  { id: 'dashboard', group: 'op' },
  { id: 'contagens', group: 'op' },
  { id: 'heatmap', group: 'op' },
  { id: 'eventos', group: 'op' },
  { id: 'utilizadores', group: 'admin' },
  { id: 'clientes', group: 'admin' },
  { id: 'dispositivos', group: 'admin' },
];

const DEFAULT_PERMISSIONS = Object.fromEntries(MODULE_IDS.map((m) => [m.id, 'none']));

// Profiles visible to each role
// const VISIBLE_PROFILES = {
//   GATEWAY_ADMIN: ['GATEWAY_ADMIN', 'CLIENT_ADMIN'],
//   CLIENT_ADMIN: ['CLIENT_ADMIN', 'USER'],
//   USER: ['CLIENT_ADMIN', 'USER'],
// };

// Role hierarchy — "Gerir permissões" only shown when current user outranks target
// const ROLE_LEVEL = { GATEWAY_ADMIN: 3, CLIENT_ADMIN: 2, USER: 1 };

const AVATAR_COLOR = '#1B75BB';

const PROFILE_STYLE = {
  GATEWAY_ADMIN: { bg: '#e8f1fa', color: '#1B75BB', border: '#c9ddf2' },
  CLIENT_ADMIN: { bg: '#d1fae5', color: '#059669', border: '#a7f3d0' },
  USER: { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

// ─── Shared toggle style ──────────────────────────────────────────────────────

const PERM_TOGGLE_SX = {
  '& .MuiToggleButtonGroup-grouped': {
    border: '1px solid #e5e7eb',
    '&:not(:first-of-type)': { borderLeft: '1px solid #e5e7eb', marginLeft: 0 },
  },
  '& .MuiToggleButton-root': {
    px: 1.25,
    py: '4px',
    fontSize: '0.75rem',
    textTransform: 'none',
    fontWeight: 400,
    color: '#6b7280',
    '&.Mui-selected': {
      bgcolor: '#e8f1fa',
      color: '#1B75BB',
      fontWeight: 500,
      borderColor: '#c9ddf2',
      '&:hover': { bgcolor: '#daeaf8' },
    },
    '&:not(.Mui-selected):hover': { bgcolor: '#f9fafb' },
  },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

// eslint-disable-next-line react/prop-types
const ProfileChip = ({ profileKey }) => {
  const { t } = useTranslation('users');
  const style = PROFILE_STYLE[profileKey] ?? PROFILE_STYLE.USER;
  const labelMap = {
    GATEWAY_ADMIN: t('profiles.gatewayAdmin'),
    CLIENT_ADMIN: t('profiles.clientAdmin'),
    USER: t('profiles.user'),
  };
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: '3px',
        borderRadius: '6px',
        bgcolor: style.bg,
        border: `1px solid ${style.border}`,
        color: style.color,
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
      }}
    >
      {labelMap[profileKey] ?? profileKey}
    </Box>
  );
};

// eslint-disable-next-line react/prop-types
const StatusBadge = ({ status, inviteTokenExpiry }) => {
  const { t } = useTranslation('common');
  const isActive = status === 'A';
  const isPending = status === 'P';
  const isExpired =
    status === 'E' ||
    (isPending && inviteTokenExpiry && new Date(inviteTokenExpiry) < new Date());

  const cfg = isExpired
    ? { bg: '#fef2f2', border: '#fecaca', color: '#dc2626', dot: '#ef4444', label: t('status.expiredInvite', 'Convite Expirado') }
    : isActive
    ? { bg: '#f0fdf4', border: '#bbf7d0', color: '#16a34a', dot: '#22c55e', label: t('status.active') }
    : isPending
    ? { bg: '#fffbeb', border: '#fde68a', color: '#d97706', dot: '#f59e0b', label: t('status.pending') }
    : { bg: '#f9fafb', border: '#e5e7eb', color: '#9ca3af', dot: '#9ca3af', label: t('status.inactive') };
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        px: '8px',
        py: '3px',
        borderRadius: '999px',
        bgcolor: cfg.bg,
        border: `1px solid ${cfg.border}`,
        color: cfg.color,
        fontSize: 12.5,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        lineHeight: 1,
      }}
    >
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: cfg.dot, flexShrink: 0 }} />
      {cfg.label}
    </Box>
  );
};

/* TODO: ManagePermissionsDialog — dialog completo de permissões por módulo, pode ser necessário no futuro
const ManagePermissionsDialog = ({ open, onClose, user }) => {
  const { t } = useTranslation('users');
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const handlePermission = (moduleId, value) => {
    if (!value) return;
    setPermissions(prev => ({ ...prev, [moduleId]: value }));
  };
  const permLevels = [
    { value: 'none',   label: t('createProfile.permissions.none') },
    { value: 'read',   label: t('createProfile.permissions.read') },
    { value: 'manage', label: t('createProfile.permissions.manage') },
  ];
  if (!user) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        {t('menu.managePermissions')}
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5, px: 3 }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2.5 }}>
          {t('managePermissions.subtitle', { name: user.name })}
        </Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
          {t('createProfile.sections.permissions')}
        </Typography>
        <Stack spacing={1}>
          {MODULE_IDS.map((mod, i) => (
            <Box key={mod.id}>
              {i > 0 && MODULE_IDS[i - 1].group !== mod.group && <Divider sx={{ my: 0.5 }} />}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: '6px', px: 1.5, borderRadius: '8px', '&:hover': { bgcolor: '#f9fafb' } }}>
                <Typography sx={{ fontSize: 13, color: 'text.primary', minWidth: 140 }}>
                  {t(`modules.${mod.id}`)}
                </Typography>
                <ToggleButtonGroup value={permissions[mod.id]} exclusive onChange={(_, v) => handlePermission(mod.id, v)} size="small" sx={PERM_TOGGLE_SX}>
                  {permLevels.map(lvl => <ToggleButton key={lvl.value} value={lvl.value}>{lvl.label}</ToggleButton>)}
                </ToggleButtonGroup>
              </Box>
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 500, color: 'text.secondary' }}>{t('createProfile.actions.cancel')}</Button>
        <Button size="small" variant="contained" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>{t('managePermissions.save')}</Button>
      </DialogActions>
    </Dialog>
  );
};
*/

// eslint-disable-next-line react/prop-types
const AssignProfileDialog = ({ open, onClose, user, onSave, currentUserRole, currentUserAuthProvider, clients = [] }) => {
  const { t } = useTranslation('users');
  const [selected, setSelected] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [allClients, setAllClients] = useState(clients);
  const [selectedClient, setSelectedClient] = useState('');
  const [initialSelectedClient, setInitialSelectedClient] = useState('');
  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [userStoreIds, setUserStoreIds] = useState([]);
  const [initialUserStoreIds, setInitialUserStoreIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setSelected(user.profile ?? '');
    setLoadingStores(true);

    const loadData = async () => {
      try {
        let targetClientId = user.clientId ?? user.ClientId ?? '';
        if (!targetClientId && user.id && user.profile !== 'GATEWAY_ADMIN') {
          try {
            const userDetail = await userAPI.getById(user.id);
            targetClientId = userDetail?.value?.clientId ?? userDetail?.value?.ClientId ?? '';
          } catch {
            // ignore
          }
        }

        setSelectedClient(targetClientId || '');
        setInitialSelectedClient(targetClientId || '');

        const [profilesRes, userStoresRes, storesRes, clientsRes] = await Promise.all([
          userAPI.listProfiles(),
          userAPI.getUserStores(user.id),
          targetClientId ? gatewayContagemAPI.listStoresByClient(targetClientId) : Promise.resolve({ value: [] }),
          clients.length === 0 ? gatewayContagemAPI.listClients() : Promise.resolve({ value: clients }),
        ]);

        setProfiles(Array.isArray(profilesRes?.value?.items) ? profilesRes.value.items : []);
        const loadedStores = Array.isArray(storesRes?.value) ? storesRes.value : [];
        setStores(loadedStores);

        const loadedUserStores = Array.isArray(userStoresRes?.value) ? userStoresRes.value : [];
        const activeStoreIds = new Set(loadedStores.map((s) => s.id));
        const validUserStores = loadedUserStores.filter((id) => activeStoreIds.has(id));

        setUserStoreIds(validUserStores);
        setInitialUserStoreIds(validUserStores);

        const loadedClients = Array.isArray(clientsRes?.value?.items)
          ? clientsRes.value.items
          : (Array.isArray(clientsRes?.value) ? clientsRes.value : (clients || []));
        setAllClients(loadedClients);
      } catch {
        setProfiles([]);
        setUserStoreIds([]);
        setInitialUserStoreIds([]);
        setStores([]);
      } finally {
        setLoadingStores(false);
      }
    };

    loadData();
  }, [open, user, clients]);

  const handleClientChange = async (newClientId) => {
    setSelectedClient(newClientId);
    if (newClientId !== initialSelectedClient) {
      setUserStoreIds([]);
    }
    if (!newClientId) {
      setStores([]);
      return;
    }
    setLoadingStores(true);
    try {
      const storesRes = await gatewayContagemAPI.listStoresByClient(newClientId);
      setStores(Array.isArray(storesRes?.value) ? storesRes.value : []);
    } catch {
      setStores([]);
    } finally {
      setLoadingStores(false);
    }
  };

  const allowedCodes = getAssignableProfileCodes(
    currentUserRole,
    user?.authProvider ?? user?.AuthProvider,
    currentUserAuthProvider
  );

  const allOptions = [
    { value: 'GATEWAY_ADMIN', label: t('profiles.gatewayAdmin') },
    { value: 'CLIENT_ADMIN', label: t('profiles.clientAdmin') },
    { value: 'USER', label: t('profiles.user') },
  ];

  const options = allOptions.filter((o) => allowedCodes.includes(o.value));

  const handleStoreToggle = (id) => {
    setUserStoreIds((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const handleToggleAllStores = () => {
    setUserStoreIds((prev) => (prev.length === 0 ? stores.map((s) => s.id) : []));
  };

  const isClientRequired = selected === 'CLIENT_ADMIN' || selected === 'USER';
  const hasClient = !isClientRequired || Boolean(selectedClient);
  const roleChanged = selected !== user?.profile;
  const clientChanged = isClientRequired && selectedClient !== initialSelectedClient;
  const storeSelectionChanged =
    userStoreIds.length !== initialUserStoreIds.length ||
    userStoreIds.some((id) => !initialUserStoreIds.includes(id));

  const canConfirm = Boolean(
    selected &&
      hasClient &&
      (roleChanged || clientChanged || (selected === 'USER' && storeSelectionChanged))
  );

  const handleSave = async () => {
    const profile = profiles.find((p) => p.code === selected);
    if (!profile) { toast.error(t('feedback.profileNotFound')); return; }
    if (isClientRequired && !selectedClient) {
      toast.error(t('managePermissions.clientRequired', 'A seleção de um cliente é obrigatória para este perfil.'));
      return;
    }

    setSaving(true);
    try {
      const targetClientIdToSend = selected === 'GATEWAY_ADMIN' ? null : selectedClient;
      const res = await userAPI.assignProfilesToUser(user.id, [profile.id], targetClientIdToSend);
      if (res?.error) { toast.error(t('feedback.errorUpdatingProfile')); return; }

      if (selected === 'USER') {
        const storeRes = await userAPI.assignStoresToUser(user.id, userStoreIds);
        if (storeRes?.error) {
          toast.error(t('feedback.errorUpdatingProfile'));
          return;
        }
      } else {
        await userAPI.assignStoresToUser(user.id, []);
      }

      const clientObj = allClients.find((c) => c.id === targetClientIdToSend);
      const newClientName = selected === 'GATEWAY_ADMIN' ? 'Gateway' : (clientObj?.name ?? user.client);
      const newStoresCount = selected === 'USER' ? (userStoreIds.length === 0 ? null : userStoreIds.length) : null;

      toast.success(t('feedback.profileUpdated'));
      onSave(selected, targetClientIdToSend, newClientName, newStoresCount);
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <ModalTemplate
      open={open}
      onClose={onClose}
      title={t('menu.managePermissions')}
      size="xs"
      isSubmitting={saving}
      onCancel={onClose}
      onConfirm={handleSave}
      canConfirm={canConfirm}
      content={
        <Stack spacing={2}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {t('managePermissions.assignSubtitle', { name: user.name })}
          </Typography>
          <RadioGroup
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            {options.map((o) => (
              <FormControlLabel
                key={o.value}
                value={o.value}
                control={<Radio size="small" />}
                label={<Typography sx={{ fontSize: 13 }}>{o.label}</Typography>}
              />
            ))}
          </RadioGroup>

          {currentUserRole === 'GATEWAY_ADMIN' && !isEntraAuthProvider(user?.authProvider ?? user?.AuthProvider) && (
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontStyle: 'italic', mt: 0.5 }}>
              {t('managePermissions.gatewayAdminEntraOnlyHint', 'O perfil de Administrador Gateway requer autenticação Microsoft Entra ID.')}
            </Typography>
          )}

          {isClientRequired && (
            <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
                {t('managePermissions.client', 'Cliente')}
              </Typography>
              <Select
                size="small"
                fullWidth
                value={selectedClient}
                onChange={(e) => handleClientChange(e.target.value)}
                displayEmpty
                sx={{ fontSize: 13, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' } }}
              >
                <MenuItem value="" disabled sx={{ fontSize: 13, color: 'text.disabled' }}>
                  {t('managePermissions.selectClient', 'Selecione um cliente')}
                </MenuItem>
                {allClients.map((c) => (
                  <MenuItem key={c.id} value={c.id} sx={{ fontSize: 13 }}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}

          {selected === 'USER' && (
            <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {t('invite.fields.stores')}
                </Typography>
                {stores.length > 0 && !loadingStores && (
                  <Typography
                    onClick={handleToggleAllStores}
                    sx={{ fontSize: 11.5, color: 'primary.main', fontWeight: 600, cursor: 'pointer' }}
                  >
                    {userStoreIds.length === 0 ? t('invite.selectAllStores') : t('invite.deselectAllStores')}
                  </Typography>
                )}
              </Box>
              {loadingStores ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : !selectedClient ? (
                <Typography sx={{ fontSize: 13, color: 'text.disabled', py: 1.5, textAlign: 'center' }}>
                  {t('managePermissions.selectClient', 'Selecione um cliente')}
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 180, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: '8px', px: 1 }}>
                  {stores.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: 'text.disabled', py: 1.5, textAlign: 'center' }}>
                      {t('invite.noStoresAvailable')}
                    </Typography>
                  ) : (
                    stores.map((s) => (
                      <FormControlLabel
                        key={s.id}
                        control={<Checkbox size="small" checked={userStoreIds.includes(s.id)} onChange={() => handleStoreToggle(s.id)} />}
                        label={
                          <ListItemText
                            primary={s.name}
                            secondary={s.city}
                            primaryTypographyProps={{ fontSize: 13, color: 'text.primary' }}
                            secondaryTypographyProps={{ fontSize: 11 }}
                          />
                        }
                        sx={{ display: 'flex', width: '100%', m: 0, py: 0.25 }}
                      />
                    ))
                  )}
                </Box>
              )}
            </Box>
          )}
        </Stack>
      }
    />
  );
};

const formatPhoneNumber = (value) => {
  if (!value) return value;
  const hasPlus = value.startsWith('+');
  const digits = value.replace(/\D/g, '');

  if (digits.startsWith('351')) {
    const countryCode = '+351';
    const rest = digits.slice(3);
    if (rest.length === 0) {
      return countryCode;
    }
    if (rest.length <= 3) {
      return `${countryCode} ${rest}`;
    }
    if (rest.length <= 6) {
      return `${countryCode} ${rest.slice(0, 3)} ${rest.slice(3)}`;
    }
    return `${countryCode} ${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6, 9)}`;
  }

  if (digits.length <= 3) {
    return (hasPlus ? '+' : '') + digits;
  }
  if (digits.length <= 6) {
    return (hasPlus ? '+' : '') + `${digits.slice(0, 3)} ${digits.slice(3)}`;
  }
  if (digits.length <= 9) {
    return (hasPlus ? '+' : '') + `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  return (hasPlus ? '+' : '') + digits;
};

// eslint-disable-next-line react/prop-types
const EditUserDialog = ({ open, onClose, user, onSaved }) => {
  const { t } = useTranslation('users');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [notifPrefs, setNotifPrefs] = useState({});
  const [notifLoading, setNotifLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const notifCategories = NOTIF_CATEGORIES[user?.profile] ?? [];

  useEffect(() => {
    if (!open || !user) return;
    setName(user.name ?? '');
    setPhone(user.phone ?? '');
    setJobTitle(user.jobTitle ?? '');

    const cats = NOTIF_CATEGORIES[user.profile] ?? [];
    if (cats.length === 0) { setNotifPrefs({}); return; }

    const defaults = Object.fromEntries(cats.map((c) => [c, false]));
    setNotifPrefs(defaults);
    setNotifLoading(true);
    userAPI.getUserNotificationPreferences(user.id)
      .then((res) => {
        const map = { ...defaults };
        (res?.value ?? res ?? []).forEach((p) => { map[p.category] = !!p.isEnabled; });
        setNotifPrefs(map);
      })
      .catch(() => {})
      .finally(() => setNotifLoading(false));
  }, [open, user]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const res = await userAPI.updateUser(user.id, {
        name: name.trim(),
        phone: phone.trim() || null,
        jobTitle: jobTitle.trim() || null,
      });
      if (res?.error) { toast.error(t('edit.error')); return; }

      if (notifCategories.length > 0) {
        const prefsPayload = Object.entries(notifPrefs)
          .map(([category, isEnabled]) => ({ category, isEnabled }));
        const prefsRes = await userAPI.updateUserNotificationPreferences(user.id, prefsPayload);
        if (prefsRes?.error) {
          toast.error(t('notificationPreferences.saveError'));
          return;
        }
      }

      toast.success(t('edit.success'));
      onSaved({ ...user, name: name.trim(), phone: phone.trim(), jobTitle: jobTitle.trim(), initials: getInitials(name.trim()) });
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <ModalTemplate
      open={open}
      onClose={onClose}
      title={t('edit.title')}
      size="xs"
      isSubmitting={saving}
      onCancel={onClose}
      onConfirm={handleSave}
      canConfirm={Boolean(name.trim())}
      content={
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            label={t('invite.fields.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            fullWidth
            required
          />
          <TextField
            label={t('invite.fields.phone')}
            value={phone}
            onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
            size="small"
            fullWidth
          />
          <TextField
            label={t('invite.fields.jobTitle')}
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            size="small"
            fullWidth
          />
          {notifCategories.length > 0 && (
            <>
              <Divider />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: 13 }}>
                    {t('notificationPreferences.title')}
                  </Typography>
                  {notifLoading && <CircularProgress size={12} />}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {notifCategories.map((cat) => (
                    <FormControlLabel
                      key={cat}
                      control={(
                        <Switch
                          size="small"
                          checked={!!notifPrefs[cat]}
                          onChange={(e) => setNotifPrefs((prev) => ({ ...prev, [cat]: e.target.checked }))}
                          disabled={notifLoading || saving}
                        />
                      )}
                      label={(
                        <Typography variant="body2" sx={{ fontSize: 13 }}>
                          {t(`notificationPreferences.categories.${cat}`)}
                        </Typography>
                      )}
                    />
                  ))}
                </Box>
              </Box>
            </>
          )}
        </Stack>
      }
    />
  );
};

// eslint-disable-next-line react/prop-types
const UserRowMenu = ({ user, onProfileChange, onUserEdited, currentUserRole, currentUserAuthProvider, clients = [] }) => {
  const { t } = useTranslation('users');
  const navigate = useNavigate();
  const { can, user: authUser } = usePermissions();
  const [anchor, setAnchor] = useState(null);
  const [permDialog, setPermDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);

  const isSelf = Boolean(authUser?.id && user.id === authUser.id);
  const canManagePerms =
    currentUserRole === 'GATEWAY_ADMIN'
      ? !isSelf
      : (ROLE_LEVEL[currentUserRole] ?? 0) > (ROLE_LEVEL[user.profile] ?? 0);
  const canSendReset =
    (currentUserRole === 'GATEWAY_ADMIN' || currentUserRole === 'CLIENT_ADMIN') &&
    user.authProvider === 'LOCAL';
  const canViewDetail = can(PERMISSIONS.USER_DETAIL_VIEW);

  const isPendingOrExpired = user.status === 'P' || user.status === 'E';

  const resetLabel = isPendingOrExpired
    ? t('menu.sendPasswordDefinition', 'Enviar definição da password')
    : t('menu.sendPasswordRedefinition', 'Enviar redefinição da password');

  const handleSendReset = async () => {
    setAnchor(null);
    setSendingReset(true);
    try {
      const result = await userAPI.sendPasswordReset(user.id);
      if (!result?.error) {
        toast.success(
          isPendingOrExpired
            ? t('menu.sendPasswordDefinitionSuccess', 'Email de definição de password enviado com sucesso.')
            : t('menu.sendPasswordRedefinitionSuccess', 'Email de redefinição enviado com sucesso.')
        );
      } else {
        toast.error(result.description ?? t('menu.sendPasswordResetError'));
      }
    } catch {
      toast.error(t('menu.sendPasswordResetError'));
    } finally {
      setSendingReset(false);
    }
  };

  const handleDeactivate = async () => {
    setAnchor(null);
    try {
      const res = await userAPI.remove(user.id);
      if (res?.error) {
        toast.error(t('menu.deactivateError', 'Erro ao desativar utilizador'));
      } else {
        toast.success(t('menu.deactivateSuccess', 'Utilizador desativado com sucesso'));
        onUserEdited({ ...user, status: 'X' });
      }
    } catch {
      toast.error(t('menu.deactivateError', 'Erro ao desativar utilizador'));
    }
  };

  const handleReactivate = async () => {
    setAnchor(null);
    try {
      const res = await userAPI.reactivate(user.id);
      if (res?.error) {
        toast.error(t('menu.reactivateError', 'Erro ao reativar utilizador'));
      } else {
        toast.success(t('menu.reactivateSuccess', 'Utilizador reativado com sucesso'));
        onUserEdited({ ...user, status: 'A' });
      }
    } catch {
      toast.error(t('menu.reactivateError', 'Erro ao reativar utilizador'));
    }
  };

  const items = [
    { key: 'edit', label: t('menu.edit'), onClick: () => { setAnchor(null); setEditDialog(true); } },
    ...(canViewDetail ? [{ key: 'details', label: t('menu.viewDetails'), onClick: () => { setAnchor(null); navigate(`/Gateway/Users/${user.id}`); } }] : []),
    ...(canManagePerms ? [{
      key: 'perms',
      label: t('menu.managePermissions'),
      onClick: () => { setAnchor(null); setPermDialog(true); },
    }] : []),
    ...(canSendReset ? [{
      key: 'reset',
      label: resetLabel,
      icon: <EmailOutlinedIcon sx={{ fontSize: 15, mr: 1, opacity: 0.7 }} />,
      onClick: handleSendReset,
    }] : []),
    ...(user.status === 'X' ? [
      { key: 'reactivate', label: t('menu.reactivate', 'Reativar'), color: 'success.main', onClick: handleReactivate }
    ] : [
      { key: 'deactivate', label: t('menu.deactivate'), color: 'error.main', onClick: handleDeactivate }
    ]),
  ];

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchor(e.currentTarget)}
        disabled={sendingReset}
        sx={{ color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}
      >
        {sendingReset
          ? <CircularProgress size={16} />
          : <MoreHorizIcon fontSize="small" />}
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { minWidth: 185 } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {items.map(({ key, label, color, icon, onClick }) => (
          <MenuItem
            key={key}
            onClick={onClick}
            sx={{ fontSize: 13, ...(color && { color }) }}
          >
            {icon ?? null}
            {label}
          </MenuItem>
        ))}
      </Menu>
      <EditUserDialog
        open={editDialog}
        onClose={() => setEditDialog(false)}
        user={user}
        onSaved={(updatedUser) => { onUserEdited(updatedUser); setEditDialog(false); }}
      />
      <AssignProfileDialog
        open={permDialog}
        onClose={() => setPermDialog(false)}
        user={user}
        currentUserRole={currentUserRole}
        currentUserAuthProvider={currentUserAuthProvider}
        clients={clients}
        onSave={(newProfile, newClientId, newClientName, newStoresCount) => {
          onProfileChange(user.id, newProfile, newClientId, newClientName, newStoresCount);
          setPermDialog(false);
        }}
      />
    </>
  );
};

// ─── Create Profile Dialog ────────────────────────────────────────────────────

// eslint-disable-next-line react/prop-types
const CreateProfileDialog = ({ open, onClose, currentUserRole, currentUserClientName, clients }) => {
  const { t } = useTranslation('users');
  const isGatewayAdmin = currentUserRole === 'GATEWAY_ADMIN';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState(isGatewayAdmin ? 'global' : 'client');
  const [selectedClient, setSelectedClient] = useState(
    isGatewayAdmin ? '' : (currentUserClientName ?? ''),
  );
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);

  useEffect(() => {
    if (open) {
      setSelectedClient(isGatewayAdmin ? '' : (currentUserClientName ?? ''));
    }
  }, [open, isGatewayAdmin, currentUserClientName]);

  const handlePermission = (moduleId, value) => {
    if (!value) return;
    setPermissions((prev) => ({ ...prev, [moduleId]: value }));
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setScope(isGatewayAdmin ? 'global' : 'client');
    setSelectedClient(isGatewayAdmin ? '' : (currentUserClientName ?? ''));
    setPermissions(DEFAULT_PERMISSIONS);
    onClose();
  };

  const canSubmit = name.trim().length > 0 && (scope === 'global' || selectedClient);

  const permLevels = [
    { value: 'none', label: t('createProfile.permissions.none') },
    { value: 'read', label: t('createProfile.permissions.read') },
    { value: 'manage', label: t('createProfile.permissions.manage') },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: '1rem',
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {t('createProfile.title')}
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5, px: 3 }}>
        {/* ── Identificação ── */}
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
          {t('createProfile.sections.identity')}
        </Typography>
        <Stack
          spacing={2}
          sx={{ mb: 3 }}
        >
          <TextField
            label={t('createProfile.form.name')}
            size="small"
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('createProfile.form.namePlaceholder')}
          />
          <TextField
            label={t('createProfile.form.description')}
            size="small"
            fullWidth
            multiline
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('createProfile.form.descriptionPlaceholder')}
          />
        </Stack>

        <Divider sx={{ mb: 2.5 }} />

        {/* ── Âmbito ── */}
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
          {t('createProfile.sections.scope')}
        </Typography>

        {isGatewayAdmin ? (
          <Stack
            spacing={1.5}
            sx={{ mb: 3 }}
          >
            <RadioGroup
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              row
            >
              <FormControlLabel
                value="global"
                control={<Radio size="small" />}
                label={<Typography sx={{ fontSize: 13 }}>{t('createProfile.scope.global')}</Typography>}
              />
              <FormControlLabel
                value="client"
                control={<Radio size="small" />}
                label={<Typography sx={{ fontSize: 13 }}>{t('createProfile.scope.perClient')}</Typography>}
              />
            </RadioGroup>
            {scope === 'client' && (
              <Select
                size="small"
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                displayEmpty
                sx={{ fontSize: 13, maxWidth: 280, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' } }}
              >
                <MenuItem
                  value=""
                  disabled
                  sx={{ fontSize: 13 }}
                >{t('createProfile.scope.selectClient')}</MenuItem>
                {(clients ?? []).map((c) => (
                  <MenuItem
                    key={c.id}
                    value={c.name}
                    sx={{ fontSize: 13 }}
                  >{c.name}</MenuItem>
                ))}
              </Select>
            )}
          </Stack>
        ) : (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {t('createProfile.scope.lockedMessage')}{' '}
              <Box
                component="span"
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                {currentUserClientName}
              </Box>
            </Typography>
          </Box>
        )}

        <Divider sx={{ mb: 2.5 }} />

        {/* ── Permissões ── */}
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
          {t('createProfile.sections.permissions')}
        </Typography>

        <Stack spacing={1}>
          {MODULE_IDS.map((mod, i) => (
            <Box key={mod.id}>
              {i > 0 && MODULE_IDS[i - 1].group !== mod.group && (
                <Divider sx={{ my: 0.5 }} />
              )}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: '6px',
                  px: 1.5,
                  borderRadius: '8px',
                  '&:hover': { bgcolor: '#f9fafb' },
                }}
              >
                <Typography sx={{ fontSize: 13, color: 'text.primary', minWidth: 140 }}>
                  {t(`modules.${mod.id}`)}
                </Typography>
                <ToggleButtonGroup
                  value={permissions[mod.id]}
                  exclusive
                  onChange={(_, v) => handlePermission(mod.id, v)}
                  size="small"
                  sx={PERM_TOGGLE_SX}
                >
                  {permLevels.map((lvl) => (
                    <ToggleButton
                      key={lvl.value}
                      value={lvl.value}
                    >
                      {lvl.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            </Box>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
        <Button
          size="small"
          onClick={handleClose}
          sx={{ textTransform: 'none', fontWeight: 500, color: 'text.secondary' }}
        >
          {t('createProfile.actions.cancel')}
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={!canSubmit}
          onClick={handleClose}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {t('createProfile.actions.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const getInitials = (name) => {
  const parts = (name ?? '').trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name ?? '').slice(0, 2).toUpperCase();
};

const UserList = () => {
  const { t } = useTranslation('users');
  const navigate = useNavigate();
  const { can, user: authUser, isInitialized } = usePermissions();
  const { clients, loadClients } = useContext(GatewayContagemContext);
  const currentUserRole = authUser?.role ?? 'USER';
  const currentUserClientId = authUser?.clientId ?? null;
  const canViewDetail = can(PERMISSIONS.USER_DETAIL_VIEW);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [profileFilter, setProfileFilter] = useState([]);
  const [clientFilter, setClientFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState(['A', 'P']);
  const [createProfileOpen, setCreateProfileOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersLoading, setUsersLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Debounce search input by 600ms to avoid firing API requests on every keypress
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 600);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const isSearchLoading = searchQuery !== debouncedSearch;
  const isLoading = usersLoading || isSearchLoading;

  // Load clients from context on init
  useEffect(() => {
    if (isInitialized) loadClients();
  }, [isInitialized, loadClients]);

  // Reset page when search or profile/client/status filter changes
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, profileFilter, clientFilter, statusFilter]);

  // Load users with server-side pagination (deduplication via ref)
  const pendingRequestRef = useMemo(() => ({}), []);

  useEffect(() => {
    if (!isInitialized) return;
    let active = true;

    const loadUsers = async () => {
      const requestKey = `${page}_${rowsPerPage}_${profileFilter.join(',')}_${clientFilter.join(',')}_${statusFilter.join(',')}_${debouncedSearch}_${refreshKey}`;

      // Return cached promise if same request is pending
      if (pendingRequestRef[requestKey]) {
        try {
          const result = await pendingRequestRef[requestKey];

          if (active) {
            processUsersResult(result);
          }
        } catch {
          if (active) {
            toast.error('Erro ao carregar utilizadores');
          }
        } finally {
          if (active) {
            setUsersLoading(false);
          }
        }

        return;
      }

      setUsersLoading(true);

      const clientIdsParam = currentUserRole === 'GATEWAY_ADMIN'
        ? (clientFilter.length > 0 && !clientFilter.includes('gateway') ? clientFilter : undefined)
        : (currentUserClientId ? [currentUserClientId] : undefined);
      const profileQueryParam = profileFilter.length === 1 ? profileFilter[0] : undefined;
      const statusQueryParam = statusFilter.length === 0 ? ['A', 'P', 'X'] : statusFilter;

      // Create promise and cache it
      const promise = userAPI.list({
        page: page + 1,
        itemsPerPage: rowsPerPage,
        search: debouncedSearch || undefined,
        status: statusQueryParam,
        ...(profileQueryParam && { profile: profileQueryParam }),
        ...(clientIdsParam && { clientIds: clientIdsParam }),
      });

      pendingRequestRef[requestKey] = promise;

      try {
        const result = await promise;
        if (!active) return;
        processUsersResult(result);
      } catch {
        if (active) toast.error('Erro ao carregar utilizadores');
      } finally {
        if (active) setUsersLoading(false);
        delete pendingRequestRef[requestKey];
      }
    };

    const processUsersResult = (result) => {
      if (!result || result.error) {
        setUsers([]);
        setUsersTotal(0);
        toast.error('Erro ao carregar utilizadores');
        return;
      }

      const paginatedResult = result.value;
      const rawUsers = Array.isArray(paginatedResult?.items) ? paginatedResult.items : [];

      // Apply client-side role/client filtering (access control)
      const visibleProfs = VISIBLE_PROFILES[currentUserRole] ?? ['CLIENT_ADMIN', 'USER'];
      const clientMap = {};
      (clients || []).forEach((c) => { clientMap[c.id] = c.name; });

      const mapped = rawUsers
        .filter((u) => {
          const profile = u.profiles?.[0]?.code ?? 'USER';
          if (!visibleProfs.includes(profile)) return false;
          if (currentUserRole !== 'GATEWAY_ADMIN' && u.clientId !== currentUserClientId) return false;

          // Client-side profile filter (if multiple are selected)
          if (profileFilter.length > 0) {
            if (!profileFilter.includes(profile)) return false;
          }

          // Client-side client filter (for gateway admin users if 'gateway' option selected)
          if (currentUserRole === 'GATEWAY_ADMIN' && clientFilter.length > 0) {
            const userClientVal = u.clientId ? u.clientId : 'gateway';
            if (!clientFilter.includes(userClientVal)) return false;
          }
          return true;
        })
        .map((u) => ({
          id: u.id,
          clientId: u.clientId ?? u.ClientId ?? null,
          name: u.name ?? '',
          email: u.email ?? '',
          phone: u.phone ?? '',
          jobTitle: u.jobTitle ?? '',
          initials: getInitials(u.name),
          profile: u.profiles?.[0]?.code ?? 'USER',
          authProvider: u.authProvider ?? u.AuthProvider ?? 'LOCAL',
          client: u.clientId ? (clientMap[u.clientId] ?? '—') : 'Gateway',
          storesCount: u.storesCount ?? u.StoresCount ?? null,
          status: u.sysStatus ?? 'A',
          inviteTokenExpiry: u.inviteTokenExpiry ?? u.InviteTokenExpiry ?? null,
        }));

      setUsers(mapped);

      const hasClientFilterClientSide = currentUserRole === 'GATEWAY_ADMIN' && clientFilter.includes('gateway');
      const hasProfileFilterClientSide = profileFilter.length > 1;

      if (hasClientFilterClientSide || hasProfileFilterClientSide) {
        setUsersTotal(mapped.length);
      } else {
        setUsersTotal(paginatedResult?.totalCount ?? mapped.length);
      }
    };

    loadUsers();
    return () => { active = false; };
  }, [isInitialized, currentUserRole, currentUserClientId, page, rowsPerPage, profileFilter.join(','), clientFilter.join(','), statusFilter.join(','), debouncedSearch, clients, pendingRequestRef, refreshKey]);

  const currentUserClientName = useMemo(() => {
    if (!currentUserClientId) return 'Gateway';
    return clients.find((c) => c.id === currentUserClientId)?.name ?? 'Gateway';
  }, [currentUserClientId, clients]);

  const handleProfileChange = (userId, newProfile, newClientId, newClientName, newStoresCount) => {
    setUsers((prev) => prev.map((u) => {
      if (u.id !== userId) return u;
      return {
        ...u,
        profile: newProfile,
        clientId: newClientId !== undefined ? newClientId : (newProfile === 'GATEWAY_ADMIN' ? null : u.clientId),
        client: newClientName !== undefined ? newClientName : (newProfile === 'GATEWAY_ADMIN' ? 'Gateway' : u.client),
        storesCount: newStoresCount !== undefined ? newStoresCount : (newProfile === 'GATEWAY_ADMIN' || newProfile === 'CLIENT_ADMIN' ? null : u.storesCount),
      };
    }));
  };

  const handleUserEdited = () => {
    setRefreshKey((k) => k + 1);
  };

  const kpi = useMemo(() => ({
    total: usersTotal,
    gatewayAdmins: users.filter(u => u.profile === 'GATEWAY_ADMIN').length,
    clientAdmins: users.filter(u => u.profile === 'CLIENT_ADMIN').length,
    standardUsers: users.filter(u => u.profile === 'USER').length,
    activeRecent: users.filter(u => u.status === 'ACTIVE').length,
  }), [users, usersTotal]);

  const allProfileOptions = [
    { value: 'GATEWAY_ADMIN', label: t('profiles.gatewayAdmin') },
    { value: 'CLIENT_ADMIN', label: t('profiles.clientAdmin') },
    { value: 'USER', label: t('profiles.user') },
  ];

  const profileOptions = allProfileOptions.filter(opt => (VISIBLE_PROFILES[currentUserRole] ?? []).includes(opt.value));

  // Apply search filter locally (not supported by backend)
  const displayedUsers = useMemo(() => {
    if (!searchQuery) return users;
    const q = searchQuery.toLowerCase();
    return users.filter((u) => {
      return (
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
      );
    });
  }, [users, searchQuery]);

  const getStoresLabel = (count) =>
  (count === null
    ? t('table.allStores')
    : t(count === 1 ? 'table.storeCount_one' : 'table.storeCount_other', { count }));

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Page header ── */}
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: 'none', fontWeight: 500, fontSize: '0.8125rem', borderColor: 'divider', color: 'text.secondary', display: 'none' }}
        >
          {t('actions.export')}
        </Button>
        {(currentUserRole === 'CLIENT_ADMIN' || currentUserRole === 'GATEWAY_ADMIN') && (
          <Button
            variant="contained"
            size="small"
            startIcon={<PersonAddAlt1OutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => setInviteOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}
          >
            {t('actions.inviteUser')}
          </Button>
        )}
      </PageHeader>

      {/* ── KPI cards ── */}
      <Grid
        container
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Grid
          item
          xs={12}
          sm={6}
          md={2.4}
        >
          <StatCard
            title={t('kpi.total')}
            value={kpi.total}
            icon={<GroupOutlinedIcon fontSize="inherit" />}
            loading={usersLoading}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={2.4}
        >
          <StatCard
            title={t('kpi.gatewayAdmins')}
            value={kpi.gatewayAdmins}
            icon={<AdminPanelSettingsOutlinedIcon fontSize="inherit" />}
            loading={usersLoading}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={2.4}
        >
          <StatCard
            title={t('kpi.clientAdmins')}
            value={kpi.clientAdmins}
            icon={<PersonOutlinedIcon fontSize="inherit" />}
            loading={usersLoading}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={2.4}
        >
          <StatCard
            title={t('kpi.standardUsers')}
            value={kpi.standardUsers}
            icon={<PersonOutlinedIcon fontSize="inherit" />}
            loading={usersLoading}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={2.4}
        >
          <StatCard
            title={t('kpi.activeRecent')}
            value={kpi.activeRecent}
            icon={<TodayOutlinedIcon fontSize="inherit" />}
            loading={usersLoading}
          />
        </Grid>
      </Grid>

      {/* ── Table card ── */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '14px',
          overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
        }}
      >
        {/* ── Filter bar ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
          >
            <TextField
              id="query-search-filter-input"
              name="query-search-filter-input"
              type="search"
              autoComplete="off"
              size="small"
              placeholder={t('filters.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 230,
                '& .MuiInputBase-root': { fontSize: 13 },
                '& .MuiOutlinedInput-root fieldset': { borderColor: 'divider' },
              }}
            />
            {currentUserRole === 'GATEWAY_ADMIN' && (
              <Select
                multiple
                size="small"
                value={clientFilter}
                onChange={(e) => {
                  const val = e.target.value;
                  setClientFilter(typeof val === 'string' ? val.split(',') : val);
                }}
                displayEmpty
                renderValue={(selected) => (
                  selected.length === 0
                    ? <span style={{ opacity: 0.55 }}>{t('filters.allClients', 'Todos os clientes')}</span>
                    : <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((id) => {
                          if (id === 'gateway') return <Chip key={id} label="Gateway" size="small" />;
                          const client = clients.find((c) => c.id === id);
                          return <Chip key={id} label={client?.name || id} size="small" />;
                        })}
                      </Box>
                )}
                sx={{
                  fontSize: 13,
                  minWidth: 170,
                  maxWidth: 280,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                }}
              >
                <MenuItem value="gateway" sx={{ fontSize: 13 }}>
                  <Checkbox checked={clientFilter.includes('gateway')} size="small" />
                  <ListItemText primary="Gateway" />
                </MenuItem>
                {(clients ?? []).map((c) => (
                  <MenuItem key={c.id} value={c.id} sx={{ fontSize: 13 }}>
                    <Checkbox checked={clientFilter.includes(c.id)} size="small" />
                    <ListItemText primary={c.name} />
                  </MenuItem>
                ))}
              </Select>
            )}
            <Select
              multiple
              size="small"
              value={statusFilter}
              onChange={(e) => {
                const val = e.target.value;
                setStatusFilter(typeof val === 'string' ? val.split(',') : val);
              }}
              displayEmpty
              renderValue={(selected) => (
                selected.length === 0
                  ? <span style={{ opacity: 0.55 }}>{t('filters.allStatuses', 'Todos os estados')}</span>
                  : <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((val) => {
                        let label = '';
                        if (val === 'A') label = t('table.active');
                        if (val === 'P') label = t('status.pending', { ns: 'common' });
                        if (val === 'E') label = t('status.expiredInvite', { ns: 'common' });
                        if (val === 'X') label = t('table.inactive');
                        return <Chip key={val} label={label} size="small" />;
                      })}
                    </Box>
              )}
              sx={{
                fontSize: 13,
                minWidth: 170,
                maxWidth: 280,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
              }}
            >
              <MenuItem value="A" sx={{ fontSize: 13 }}>
                <Checkbox size="small" checked={statusFilter.includes('A')} />
                <ListItemText primary={t('table.active')} />
              </MenuItem>
              <MenuItem value="P" sx={{ fontSize: 13 }}>
                <Checkbox size="small" checked={statusFilter.includes('P')} />
                <ListItemText primary={t('status.pending', { ns: 'common' })} />
              </MenuItem>
              <MenuItem value="E" sx={{ fontSize: 13 }}>
                <Checkbox size="small" checked={statusFilter.includes('E')} />
                <ListItemText primary={t('status.expiredInvite', { ns: 'common' })} />
              </MenuItem>
              <MenuItem value="X" sx={{ fontSize: 13 }}>
                <Checkbox size="small" checked={statusFilter.includes('X')} />
                <ListItemText primary={t('table.inactive')} />
              </MenuItem>
            </Select>
            <Select
              multiple
              size="small"
              value={profileFilter}
              onChange={(e) => {
                const val = e.target.value;
                setProfileFilter(typeof val === 'string' ? val.split(',') : val);
              }}
              displayEmpty
              renderValue={(selected) => (
                selected.length === 0
                  ? <span style={{ opacity: 0.55 }}>{t('filters.allProfiles')}</span>
                  : <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((val) => {
                        const label = allProfileOptions.find(opt => opt.value === val)?.label || val;
                        return <Chip key={val} label={label} size="small" />;
                      })}
                    </Box>
              )}
              sx={{
                fontSize: 13,
                minWidth: 170,
                maxWidth: 280,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
              }}
            >
              {profileOptions.map((opt) => (
                <MenuItem
                  key={opt.value}
                  value={opt.value}
                  sx={{ fontSize: 13 }}
                >
                  <Checkbox checked={profileFilter.includes(opt.value)} size="small" />
                  <ListItemText primary={opt.label} />
                </MenuItem>
              ))}
            </Select>
            {(searchQuery || profileFilter.length > 0 || clientFilter.length > 0 || statusFilter.length !== 2 || !statusFilter.includes('A') || !statusFilter.includes('P')) && (
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setSearchQuery('');
                  setProfileFilter([]);
                  setClientFilter([]);
                  setStatusFilter(['A', 'P']);
                }}
                sx={{
                  textTransform: 'none',
                  color: 'primary.main',
                  fontSize: 13,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                }}
              >
                {t('filters.clearFilters')}
              </Button>
            )}
          </Stack>

          <Typography sx={{ fontSize: 13, color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {t('filters.count', { count: isLoading ? 0 : displayedUsers.length })}
          </Typography>
        </Box>

        {/* ── Table ── */}
        <LoadingSkeleton loading={isLoading} preset="table" height={360} sx={{ borderRadius: 0 }}>
          <TableContainer sx={{ minHeight: 250 }}>
            <Table
              size="small"
              sx={{ '& .MuiTableCell-root': { fontSize: 13, borderColor: 'divider' } }}
            >
            <TableHead>
              <TableRow sx={TABLE_HEAD_ROW_SX}>
                <TableCell sx={{ pl: 2.5, width: '32%' }}>{t('table.user')}</TableCell>
                <TableCell sx={{ width: '22%' }}>{t('table.profile')}</TableCell>
                <TableCell sx={{ width: '18%' }}>{t('table.client')}</TableCell>
                <TableCell sx={{ width: '15%' }}>{t('table.stores')}</TableCell>
                <TableCell sx={{ width: '13%' }}>{t('table.status')}</TableCell>
                <TableCell sx={{ width: 52, pr: 1.5 }} />
              </TableRow>
            </TableHead>

            <TableBody
              sx={{
                transition: 'opacity 0.25s ease-in-out',
                opacity: isLoading ? 0 : 1,
                pointerEvents: isLoading ? 'none' : 'auto',
              }}
            >
              {displayedUsers.map((user) => (
                <TableRow
                  key={user.id}
                  sx={{
                    '&:last-child .MuiTableCell-root': { borderBottom: 'none' },
                    '&:hover': { bgcolor: '#f9fafb' },
                  }}
                >
                  <TableCell sx={{ pl: 2.5 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                    >
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          fontSize: 12,
                          fontWeight: 700,
                          bgcolor: AVATAR_COLOR,
                          flexShrink: 0,
                        }}
                      >
                        {user.initials}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          component="a"
                          onClick={() => {
                            if (canViewDetail) {
                              navigate(`/Gateway/Users/${user.id}`);
                            }
                          }}
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: 'text.primary',
                            textDecoration: 'none',
                            cursor: canViewDetail ? 'pointer' : 'default',
                            '&:hover': canViewDetail ? { color: 'primary.main', textDecoration: 'underline' } : {},
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                          }}
                        >
                          {user.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: 'text.disabled',
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {user.email}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <ProfileChip profileKey={user.profile} />
                  </TableCell>

                  <TableCell>
                    <Typography sx={{ fontSize: 13, color: 'text.primary' }}>
                      {user.client}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                      {getStoresLabel(user.storesCount)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <StatusBadge status={user.status} inviteTokenExpiry={user.inviteTokenExpiry} />
                  </TableCell>

                  <TableCell sx={{ pr: 1.5, width: 52 }}>
                    <UserRowMenu
                      user={user}
                      onProfileChange={handleProfileChange}
                      onUserEdited={handleUserEdited}
                      currentUserRole={currentUserRole}
                      currentUserAuthProvider={authUser?.authProvider ?? authUser?.authType}
                      clients={clients}
                    />
                  </TableCell>
                </TableRow>
              ))}

              {!isLoading && displayedUsers.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    sx={{ py: 5, textAlign: 'center', border: 'none' }}
                  >
                    <Typography
                      variant="body2"
                      color="text.disabled"
                    >
                      {t('table.empty')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </LoadingSkeleton>

        <TablePagination
          component="div"
          count={usersTotal}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
          showFirstButton
          showLastButton
          labelRowsPerPage={t('common:pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common:pagination.of')} ${count}`}
          sx={{ borderTop: '1px solid', borderColor: 'divider', fontSize: 13 }}
        />
      </Box>

      <CreateProfileDialog
        open={createProfileOpen}
        onClose={() => setCreateProfileOpen(false)}
        currentUserRole={currentUserRole}
        currentUserClientName={currentUserClientName}
        clients={clients}
      />

      <InviteUserDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSuccess={() => setRefreshKey((k) => k + 1)}
        currentUserRole={currentUserRole}
        currentUserClientId={currentUserClientId}
      />
    </Box>
  );
};

export default UserList;
